import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
} from '@mui/material';
import { Search, Star, Clock, Award, Eye } from 'lucide-react';
import { Project } from '../../../types/project.types';
import { JudgeAssignmentStatus } from '../../../types/judging.types';
import { judgingService } from '../../../services/judging.service';
import { getCategories } from '../../../services/categories.services';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getFairs } from '../../../services/fairs.services';
import { useAuth } from '../../../contexts/AuthContext';
import { Judges } from '../../../types/fair.types';
import { getJudgeFairs } from '../../../services/fairs.services';
import {
  fetchScoresByProjectIds,
  computeStats,
} from '../../../services/projectScores.service';

interface ProjectWithStatus extends Project {
  judgingStatus: JudgeAssignmentStatus | null;
  judgeCount?: number;
}

interface fairs {
  id: string;
  judges?: Judges[];
  status: 'inProgress' | 'completed' | 'pending';
}

interface ProjectScoringListProps {
  onViewProject: (project: Project, isAssigned: boolean) => void;
}

const ProjectScoringList: React.FC<ProjectScoringListProps> = ({
  onViewProject,
}) => {
  const { authStatus } = useAuth();
  const [fairCheckMessage, setFairCheckMessage] = useState<string | null>(null);
  const [assignedProjects, setAssignedProjects] = useState<ProjectWithStatus[]>(
    []
  );
  const [allProjects, setAllProjects] = useState<ProjectWithStatus[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allCategories, setAllCategories] = useState<
    { id: string; name: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'assigned' | 'completed' | 'unscored'
  >('assigned');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fairs, setFairs] = useState<fairs[]>([]);

  const loadAllProjects = useCallback(async () => {
    if (!authStatus.user?.uid) return;

    try {
      setLoading(true);
      const projectsWithStatus =
        await judgingService.getProjectsWithJudgingStatus(authStatus.user.uid);
      console.log('Loaded projects with status:', projectsWithStatus);

      // Fetch scores for all projects to get judge counts
      const projectIds = projectsWithStatus.map((p) => p.id);
      const rawScores = await fetchScoresByProjectIds(projectIds);
      const stats = computeStats(rawScores);

      // Attach judgeCount to each project
      const projectsWithCounts = projectsWithStatus.map((p) => ({
        ...p,
        judgeCount: stats[p.id]?.judgeCount ?? 0,
      }));

      setAllProjects(projectsWithCounts);
      console.log('All projects with counts:', projectsWithCounts);

      setError(null);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    const loadProjects = async () => {
      if (!authStatus.user?.uid) return;

      try {
        setLoading(true);
        const judgeId = authStatus.user.uid;
        const judgeCategories = authStatus.user.categories || [];
        const regionId = authStatus.metadata?.regionId;

        if (!regionId) throw new Error('Region ID is required to fetch fairs.');

        const fairs = await getJudgeFairs(judgeId);
        // Map the fairs to match the expected 'fairs' structure
        const mappedFairs = fairs.map((fair) => ({
          ...fair,
          status:
            fair.status === 'active'
              ? 'inProgress'
              : fair.status === 'completed'
                ? 'completed'
                : 'pending',
        })) as fairs[];
        setFairs(mappedFairs);

        if (fairs.length === 0) {
          setFairCheckMessage(
            'You must sign up for a fair before you can score projects.'
          );
          return;
        }

        const activeFair = fairs.find((f) => f.status === 'inProgress');
        if (!activeFair) {
          setFairCheckMessage(
            'The fair you are assigned to has not started yet.'
          );
          return;
        }

        const fairId = activeFair.id;
        const fetchedCategories = await getCategories(regionId);
        setAllCategories(fetchedCategories);

        if (statusFilter === 'all') {
          await loadAllProjects(); // use the original function
          return;
        }

        // Guard clause for no assigned categories
        if (!judgeCategories || judgeCategories.length === 0) {
          setAssignedProjects([]);
          setFairCheckMessage('you have no assigned categories yet.');
          setLoading(false);
          return;
        }

        const { direct, linked } =
          await judgingService.getJudgeProjectsByCategory(
            judgeId,
            fairId,
            judgeCategories,
            fetchedCategories
          );

        const merged = [
          ...direct.map((p) => ({
            ...p,
            judgingStatus: 'pending' as JudgeAssignmentStatus,
          })),
          ...linked.map((p) => ({
            ...p,
            judgingStatus: 'pending' as JudgeAssignmentStatus,
          })),
        ];

        const rawScores = await fetchScoresByProjectIds(
          merged.map((p) => p.id)
        );

        // Compute judge counts
        const stats = computeStats(rawScores);

        // Attach judgeCount to each project
        const mergedWithCounts = merged.map((p) => ({
          ...p,
          judgeCount: stats[p.id]?.judgeCount ?? 0,
        }));

        setAssignedProjects(mergedWithCounts);

        console.log('Assigned projects with counts:', mergedWithCounts);
        setFairCheckMessage(null);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Failed to load projects. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [
    authStatus.user?.uid,
    authStatus.user?.regionId,
    authStatus.user?.categories,
    authStatus.metadata?.regionId,
    statusFilter,
    loadAllProjects,
  ]);

  const filteredProjects = (() => {
    // First, determine the base set of projects based on status filter
    let baseProjects;

    switch (statusFilter) {
      case 'assigned':
        baseProjects = assignedProjects; // Show all assigned projects
        break;
      case 'all':
        baseProjects = allProjects;
        break;
      case 'completed':
        baseProjects = allProjects.filter(
          (p) => p.judgingStatus === 'completed'
        );
        break;
      case 'unscored':
        baseProjects = allProjects.filter(
          (p) => p.judgingStatus !== 'completed'
        );
        break;
      default:
        baseProjects = allProjects;
        break;
    }

    console.log('Base projects after status filter:', baseProjects);

    // Apply search term filtering
    const filtered = baseProjects.filter((project) =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort by judge count in ascending order (projects with fewer judges first)
    const sorted = [...filtered].sort((a, b) => {
      const countA = a.judgeCount !== undefined ? a.judgeCount : 0;
      const countB = b.judgeCount !== undefined ? b.judgeCount : 0;
      return countA - countB; // Ascending order
    });

    console.log('Projects after sorting by judge count:', sorted);

    return sorted;
  })();

  const getStatusChip = (status: JudgeAssignmentStatus | null) => {
    switch (status) {
      case 'completed':
        return (
          <Chip
            icon={<Star size={16} />}
            label="Scored"
            color="success"
            size="small"
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<Clock size={16} />}
            label="Assigned"
            color="primary"
            size="small"
          />
        );
      case 'in_progress':
        return (
          <Chip
            icon={<Clock size={16} />}
            label="In Progress"
            color="warning"
            size="small"
          />
        );
      default:
        return (
          <Chip icon={<Award size={16} />} label="Available" size="small" />
        );
    }
  };

  const handleViewProject = (project: ProjectWithStatus) => {
    onViewProject(project, project.judgingStatus === 'pending');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        Project Scoring
      </Typography>

      {fairCheckMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {fairCheckMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <Search size={20} className="mr-2 text-gray-500" />
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value as typeof statusFilter)
              }
            >
              <MenuItem value="all">All Projects</MenuItem>
              <MenuItem value="assigned">Assigned to Me</MenuItem>
              <MenuItem value="completed">Scored</MenuItem>
              <MenuItem value="unscored">Not Scored</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Students</TableCell>
              <TableCell align="center">Judges Count</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading projects...
                </TableCell>
              </TableRow>
            ) : filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No projects found
                </TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell>{project.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        project.fairType === 'highSchool'
                          ? 'High School'
                          : 'Middle School'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {project.members
                      .map((member) => `${member.firstName} ${member.lastName}`)
                      .join(', ')}
                  </TableCell>
                  <TableCell align="center">
                    {project.judgeCount !== undefined ? project.judgeCount : 0}
                  </TableCell>
                  <TableCell>{getStatusChip(project.judgingStatus)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="View Project"
                      onClick={() => handleViewProject(project)}
                    >
                      <Eye size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProjectScoringList;
